import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { IMAGE_LOADER, ImageLoaderConfig, isPlatformBrowser, NgIf, NgOptimizedImage } from '@angular/common';
import { Platform } from '@angular/cdk/platform';

import { environment } from '../../../../environments/environment';
import { WindowDetectService } from '@service/window-detect.service';
import { ImageUrl } from '@common/pipes';
import { ImageSize } from '@helper/image-size';
import { Strings } from '@helper/strings';

@Component({
  imports: [NgOptimizedImage, NgIf, ImageUrl],
  selector: 'app-image-loading',
  templateUrl: './image-loading.component.html',
  styleUrls: ['./image-loading.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig): string => {
        if (config.src.startsWith('http') || config.src.startsWith('https') || config.src.startsWith('assets')) {
          return config.src;
        }
        if (config.loaderParams?.['originalQuality']) {
          return new URL(`${environment.fileScheme}/${config.src}`).href;
        }
        const originalWidth = Number(config.src.substring(0, 4));
        const width = config.width ?? 0;
        if (isNaN(originalWidth) || !originalWidth || !width) {
          return new URL(`${environment.fileScheme}/${config.src}`).href;
        }
        const one = originalWidth / 3;
        const two = (originalWidth * 2) / 3;
        if (width <= one) {
          return new URL(`${environment.fileScheme}/${config.src}@1x`).href;
        } else if (one < width && width <= two) {
          return new URL(`${environment.fileScheme}/${config.src}@2x`).href;
        }
        return new URL(`${environment.fileScheme}/${config.src}`).href;
      },
    },
  ],
})
export class ImageLoadingComponent implements OnChanges {
  readonly loading = computed(() => (this.windowService.isMobileDevice() ? 'auto' : this.priority ? 'eager' : 'lazy'));
  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly imgWidthCss = signal('');
  readonly imgHeightCss = signal('');
  readonly isMobile = this.windowService.isMobileDevice();

  @Input({ required: true }) src = '';
  @Input({ required: true }) alt = '';
  @Input({ required: true }) clickable = false;
  @Input() selectable = true;
  @Input() tooltip = '';
  @Input() targetWidth = 0;
  @Input() targetHeight = 0;
  @Input() priority = false;
  @Input() borderRadius = 0;
  @Input() fill = false;
  @Input() middle = false;
  @Input() originalQuality = false;
  @Input() widthCss = '';
  @Input() heightCss = '';
  @Input() ssrSuffix: ImageSize = '';

  @Output() readonly imageLoaded = new EventEmitter<void>();

  size: [number, number] | undefined;

  constructor(
    public platform: Platform,
    private hostElement: ElementRef,
    private windowService: WindowDetectService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnChanges(): void {
    const size = this.calculateWidthHeight();
    if (
      this.targetWidth &&
      this.targetHeight &&
      size &&
      (size[0] !== this.targetWidth || size[1] !== this.targetHeight)
    ) {
      this.size = size;
      this.hostElement.nativeElement.style.display = 'flex';
      this.hostElement.nativeElement.style.alignItems = 'center';
      if (this.isBrowser) {
        this.hostElement.nativeElement.style.width = `${this.targetWidth}px`;
        this.hostElement.nativeElement.style.height = `${this.targetHeight}px`;
      } else {
        this.setSsrCss(this.size);
        this.hostElement.nativeElement.style.width = this.imgWidthCss();
        this.hostElement.nativeElement.style.height =
          this.heightCss || `calc(${this.widthCss} * ${this.targetHeight / this.targetWidth})`;
      }
    } else {
      this.size = size ?? [this.targetWidth, this.targetHeight];
      if (this.size[0] && this.size[1]) {
        this.hostElement.nativeElement.style.display = 'inline-block';
        if (this.isBrowser) {
          this.hostElement.nativeElement.style.width = `${this.size[0]}px`;
          this.hostElement.nativeElement.style.height = `${this.size[1]}px`;
        } else {
          this.setSsrCss(this.size);
          this.hostElement.nativeElement.style.width = this.imgWidthCss();
          this.hostElement.nativeElement.style.height = this.imgHeightCss();
        }
      }
    }
  }

  onImageLoaded(): void {
    this.imageLoaded.emit();
  }

  private calculateWidthHeight(): [number, number] | undefined {
    if (!this.src) {
      return undefined;
    }
    const size = Strings.getWidthHeightFromHash(this.src);
    if (!size[0] || !size[1]) {
      return undefined;
    }
    if (this.targetWidth) {
      const targetHeight = (size[1] / size[0]) * this.targetWidth;
      return [this.targetWidth, targetHeight];
    } else if (this.targetHeight) {
      const targetWidth = (size[0] / size[1]) * this.targetHeight;
      return [targetWidth, this.targetHeight];
    }
    return undefined;
  }

  private setSsrCss(size: [number, number]): void {
    if (!this.widthCss) {
      this.imgWidthCss.set(`${size[0]}px`);
      this.imgHeightCss.set(`${size[1]}px`);
    } else {
      this.imgWidthCss.set(this.widthCss);
      this.imgHeightCss.set(this.heightCss || `calc(${this.widthCss} * ${size[1] / size[0]})`);
    }
  }
}
